import React, { useEffect, useState, MouseEvent, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import BottomArrow from "@assets/icons/bottomArrow";
import LoadMoreButton from "@components/ui-kit/LoadMoreButton";
import ProductCode from "@components/ProductCode";
import ProblemsBlock from "@components/ProblemsBlock";
import { ProductsService } from "@services/amazon/products/products.service";
import { ProductWithImageLink } from "@services/amazon/products/types";
import { useProductStore } from "src/store/overviewProduct.state";
import { useInsightsStore } from "src/store/insights/insights.state";
import { formattedKeys } from "src/utils/convertUtil";
import { SpiderService } from "@services/spider/spider.services";
import { SettingsModal } from "@components/ExecutiveSummary/SettingsBigModal/index";
import { useWidgets } from "@hooks/useWidgets";
import {
  defaultWidgetsProducts,
  useDashboardStore,
} from "@pages/Dashboard/store/dashboard.state";
import { WIDGET_METRICS_CONFIG } from "@services/math/math.const";
import { ROUTES } from "src/router/routes";
import { Spin } from "antd";
import Settings from "@assets/img/settings";
import { useIsMobile } from "src/utils/isMobile";
import { widgetService } from "@services/widget.service";
import { WidgetMetricKey } from "@services/math/math.service";

export interface ProductWithMetrics extends ProductWithImageLink {
  totalSales?: number;
  totalSpend?: number;
  totalCvr?: number;
  totalOrders?: number;
  totalUnitsSold?: number;
  roi?: number;
  tacos?: number;
  contributionProfit?: number;
  contributionMargin?: number;
}

const COLUMN_MIN_WIDTH = 80;

type TableProps = { showOnly?: "COO" | "Market" | "CFO" | "Ad" };

const Table: React.FC<TableProps> = ({ showOnly }) => {
  const { setSelectedProduct } = useProductStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isCRO = location.pathname === `/${ROUTES.CRO}`;
  const { dateRange } = useDashboardStore((state) => ({
    dateRange: state.dateRange,
  }));

  const [choosedWidgets, setChoosedWidgets] = useState<WidgetMetricKey[]>(
    defaultWidgetsProducts,
  );
  const selectedMetrics = choosedWidgets.map((key) => ({
    key,
    name: WIDGET_METRICS_CONFIG[key].label,
    info: WIDGET_METRICS_CONFIG[key].info,
  }));
  const { handleApply } = useWidgets();
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const allowedGroups = showOnly ? [showOnly] : undefined;
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });

  const [visibleProducts, setVisibleProducts] = useState(10);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState<ProductWithMetrics[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [metricsMap, setMetricsMap] = useState<
    Record<string, Record<WidgetMetricKey, number>>
  >({});

  const productsService = new ProductsService();
  const { setProductOneSpiderTitles, setProductOneSpiderValues } =
    useInsightsStore();
  const [selectedAsin, setSelectedAsin] = useState<string | null>(null);

  const originalIsMobile = useIsMobile();
  const isMobile = originalIsMobile || window.innerWidth <= 1024;

  const fetchProducts = async (page = 1, limit = 10) => {
    try {
      const resp = await productsService.getOwnProducts({ page, limit });
      setProducts(resp as ProductWithMetrics[]);
      if (resp.length === limit && limit < 32) {
        await fetchProducts(page, limit === 8 ? 16 : 32);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!products.length) return;
    const dateStart = dateRange.startDate.toISOString().split("T")[0];
    const dateEnd = dateRange.endDate.toISOString().split("T")[0];
    products.forEach(async (p) => {
      try {
        const data = await widgetService.getWidgetsMetrics({
          dateStart,
          dateEnd,
          asin: p.asin,
        });
        const completeData: Record<WidgetMetricKey, number> = {} as any;
        choosedWidgets.forEach(
          (k) => (completeData[k] = (data as any)[k] ?? 0),
        );
        setMetricsMap((prev) => ({ ...prev, [p.asin]: completeData }));
      } catch {
        const emptyData: Record<WidgetMetricKey, number> = {} as any;
        choosedWidgets.forEach((k) => (emptyData[k] = 0));
        setMetricsMap((prev) => ({ ...prev, [p.asin]: emptyData }));
      }
    });
  }, [products, choosedWidgets]);

  const fetchSpider = async (asin: string) => {
    try {
      const dots = await new SpiderService().getSpiderInfo(asin);
      if (dots && Object.keys(dots).length) {
        setProductOneSpiderTitles(formattedKeys(dots));
        setProductOneSpiderValues(
          Object.values(dots).map((v) => +v.toFixed(1)),
        );
      } else {
        setProductOneSpiderTitles([]);
        setProductOneSpiderValues([]);
      }
    } catch {
      setProductOneSpiderTitles([]);
      setProductOneSpiderValues([]);
    }
  };

  useEffect(() => {
    if (selectedAsin) fetchSpider(selectedAsin);
    else {
      setProductOneSpiderTitles([]);
      setProductOneSpiderValues([]);
    }
  }, [selectedAsin]);

  const toggleExpand = (pr: ProductWithMetrics) => {
    if (expandedProductId === pr.asin) {
      setExpandedProductId(null);
      setSelectedProduct(null);
      setSelectedAsin(null);
    } else {
      setExpandedProductId(pr.asin);
      setSelectedProduct(pr);
      setSelectedAsin(pr.asin);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setLoadingMore(false);
      setVisibleProducts((v) => v + 10);
    }, 1000);
  };

  const openConfigModal = (e: MouseEvent<HTMLButtonElement>) => {
    const { right, bottom } = e.currentTarget.getBoundingClientRect();
    const modalWidth = 300;
    const modalHeight = 400;
    const left = Math.min(
      Math.max(right, 10),
      window.innerWidth - modalWidth - 10,
    );
    const top = Math.min(
      Math.max(bottom, 10),
      window.innerHeight - modalHeight - 10,
    );
    setModalPos({ x: left, y: top });
    setIsConfigModalOpen(true);
  };

  const closeConfigModal = () => setIsConfigModalOpen(false);

  const applySettings = useCallback(() => {
    handleApply(choosedWidgets);
    setIsConfigModalOpen(false);
  }, [choosedWidgets, handleApply]);

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.loading}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {!isMobile ? (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.headerCell}>PRODUCT</th>
                    {selectedMetrics.map((m) => (
                      <th
                        key={m.key}
                        className={styles.headerCell}
                        style={{
                          minWidth: COLUMN_MIN_WIDTH,
                          paddingRight:
                            m.name.trim().split(/\s+/).length === 2
                              ? 5
                              : undefined,
                        }}
                      >
                        {m.name}
                      </th>
                    ))}
                    <th
                      className={`${styles.headerCell} ${styles.settingsCell}`}
                    >
                      <button
                        onClick={openConfigModal}
                        className={styles.settingsBtn}
                      >
                        <Settings />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, visibleProducts).map((pr) => {
                    const m = metricsMap[pr.asin] || {};
                    const allLoaded = choosedWidgets.every((k) => k in m);
                    return (
                      <React.Fragment key={pr.asin}>
                        <tr
                          className={`${styles.row} ${
                            expandedProductId === pr.asin ? styles.expanded : ""
                          }`}
                        >
                          <td className={styles.cell}>
                            <div className={styles.productInfo}>
                              <img
                                src={pr.image_link}
                                alt={pr.item_name}
                                className={styles.productImage}
                              />
                              <div className={styles.productDetails}>
                                <button
                                  onClick={() => navigate("/my-product")}
                                  className={styles.titleButton}
                                >
                                  {pr.item_name}
                                </button>
                                <ProductCode code={pr.asin} />
                              </div>
                            </div>
                          </td>
                          {choosedWidgets.map((key) => {
                            const hasValue = key in m;
                            return (
                              <td key={key} className={styles.cell}>
                                <div className={styles.innerCell}>
                                  {hasValue ? (
                                    widgetService.formatValue(key, m[key])
                                  ) : (
                                    <Spin size="small" />
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className={styles.cell}>
                            {allLoaded && (
                              <button
                                onClick={() => toggleExpand(pr)}
                                className={`${styles.expandButton} ${
                                  expandedProductId === pr.asin
                                    ? styles.rotated
                                    : ""
                                }`}
                              >
                                <BottomArrow />
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedProductId === pr.asin && allLoaded && (
                          <tr className={styles.expandedRow}>
                            <td
                              colSpan={choosedWidgets.length + 2}
                              className={styles.expandedCell}
                            >
                              <ProblemsBlock intelligence={false} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.mobileContainer}>…</div>
          )}

          <footer className={styles.footer} />

          {loadingMore && (
            <div className={styles.loading}>
              <Spin size="large" />
            </div>
          )}
          {visibleProducts < products.length && (
            <LoadMoreButton onClick={handleLoadMore} />
          )}

          {isConfigModalOpen && (
            <div className={styles.overlay} onClick={closeConfigModal}>
              <div
                className={styles.modalContainer}
                style={{ left: modalPos.x, top: modalPos.y }}
                onClick={(e) => e.stopPropagation()}
              >
                <SettingsModal
                  open={isConfigModalOpen}
                  onClose={closeConfigModal}
                  onApply={applySettings}
                  choosedWidgets={choosedWidgets}
                  setChoosedWidgets={setChoosedWidgets}
                  maxWidgets={Infinity}
                  allowedGroups={allowedGroups}
                  isCRO={isCRO}
                  showHistorical={showHistorical}
                  setShowHistorical={setShowHistorical}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
